import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Trophy, Clock, Users, ChevronRight, Eye, BarChart3, List, Timer, Medal, Flag, Wifi, WifiOff, Maximize, Minimize, Monitor, Lock, CheckCircle, Globe, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// ============= TRANSLATIONS =============
const spectatorTranslations = {
  en: {
    rankings: 'Rankings', competitorsScored: 'competitors scored', competitor: 'Competitor',
    duration: 'Duration', card: 'Card', score: 'Score', noResultsYet: 'No results yet',
    currentCompetitor: 'Current Competitor', of: 'of', performing: 'PERFORMING',
    awaitingResults: 'AWAITING RESULTS', countdownToOT: 'Countdown to Official TOP', officialTop: 'Official Top',
    upNext: 'Up Next', noMoreCompetitors: 'No more competitors', progress: 'Event Progress',
    eventDuration: 'Event Duration', runningOrder: 'Running Order', status: 'Status',
    done: 'Done', live: 'Live', awaiting: 'Awaiting', waiting: 'Waiting',
    liveDashboard: 'Live Dashboard', scoreboard: 'Scoreboard', presentationMode: 'Presentation Mode',
    allPassagesCompleted: 'All passages completed!', timeBeforeProtest: 'Time Before Protest',
    protestInProgress: 'Protest Period in Progress', protestEnded: 'Protest period has ended',
    resultsAreFinal: 'Results are now final.', eventClosed: 'Event Closed',
    autoRotating: 'Auto-rotating every 15s', exit: 'Exit',
    performanceInProgress: 'Performance in progress...', speed: 'Speed', nationality: 'Nat.', speedSplits: '25m Speed Splits', globalSpeed: 'Global Speed',
    validationByJudges: 'Performance in validation by Judges', performanceSaved: 'Performance Saved', eventTerminated: 'Event Completely Terminated!', terminateEvent: 'Terminate Event', finalResults: 'Final Results',
    speedComparison: 'Speed Comparison', currentAthlete: 'Current', leader: 'Leader', noLeaderYet: 'No completed performance yet', distanceMeters: 'Distance (m)', speedMs: 'Speed (m/s)',
  },
  fr: {
    rankings: 'Classement', competitorsScored: 'competiteurs notes', competitor: 'Competiteur',
    duration: 'Duree', card: 'Carton', score: 'Score', noResultsYet: 'Aucun resultat',
    currentCompetitor: 'Competiteur actuel', of: 'sur', performing: 'EN COURS',
    awaitingResults: 'EN ATTENTE', countdownToOT: 'Compte a rebours Top Officiel', officialTop: 'Top Officiel',
    upNext: 'Suivant', noMoreCompetitors: 'Plus de competiteurs', progress: 'Progression de l\'epreuve',
    eventDuration: "Duree de l'epreuve", runningOrder: 'Ordre de passage', status: 'Statut',
    done: 'Termine', live: 'En direct', awaiting: 'Attente', waiting: 'En attente',
    liveDashboard: 'Tableau de bord', scoreboard: 'Classement', presentationMode: 'Mode Presentation',
    allPassagesCompleted: 'Tous les passages sont termines!', timeBeforeProtest: 'Temps avant protestation',
    protestInProgress: 'Periode de protestation en cours', protestEnded: 'Periode de protestation terminee',
    resultsAreFinal: 'Les resultats sont definitifs.', eventClosed: 'Epreuve fermee',
    autoRotating: 'Rotation auto toutes les 15s', exit: 'Quitter',
    performanceInProgress: 'Performance en cours...', speed: 'Vitesse', nationality: 'Nat.', speedSplits: 'Splits 25m', globalSpeed: 'Vitesse Globale',
    validationByJudges: 'Performance en validation par les Juges', performanceSaved: 'Performance enregistree', eventTerminated: 'Epreuve completement terminee!', terminateEvent: 'Terminer l\'epreuve', finalResults: 'Resultats finaux',
    speedComparison: 'Comparaison de vitesse', currentAthlete: 'Actuel', leader: 'Leader', noLeaderYet: 'Aucune performance terminee', distanceMeters: 'Distance (m)', speedMs: 'Vitesse (m/s)',
  }
};

function formatTime(seconds) {
  if (!seconds && seconds !== 0) return '--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatPerformance(val, unit) {
  if (val == null) return '-';
  if (unit === 'time' || unit === 'seconds') return formatTime(val);
  return `${val}m`;
}

function formatTimeHHMM(dateValue) {
  if (!dateValue) return '-';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return '-'; }
}

function CardBadge({ card }) {
  if (!card) return null;
  const colors = {
    white: 'bg-white text-gray-800 border border-gray-300',
    yellow: 'bg-yellow-400 text-yellow-900',
    red: 'bg-red-600 text-white',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${colors[card] || 'bg-gray-500 text-white'}`}>
      {card}
    </span>
  );
}

// ============= SCOREBOARD VIEW =============
function ScoreboardView({ data, t }) {
  const { rankings, event_type, competition } = data;
  return (
    <div className="space-y-4" data-testid="scoreboard-view">
      <div className="flex items-center gap-3 mb-2">
        <Trophy className="w-5 h-5 text-yellow-400" />
        <h2 className="text-xl font-bold text-white">{t('rankings')}</h2>
        <span className="text-sm text-gray-400">{rankings.length} {t('competitorsScored')}</span>
        <span className="ml-auto text-sm text-cyan-400 font-medium">{event_type?.name || event_type?.code}</span>
      </div>
      <div className="bg-gray-900/60 rounded-xl overflow-hidden border border-gray-700/50">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 border-b border-gray-700/50">
              <th className="px-4 py-3 text-left w-16">#</th>
              <th className="px-4 py-3 text-left">{t('competitor')}</th>
              <th className="px-4 py-3 text-center">Nat.</th>
              <th className="px-4 py-3 text-center">AP</th>
              <th className="px-4 py-3 text-center">RP</th>
              <th className="px-4 py-3 text-center">{t('duration')}</th>
              <th className="px-4 py-3 text-center">{t('card')}</th>
              <th className="px-4 py-3 text-right">{t('score')}</th>
            </tr>
          </thead>
          <tbody>
            {rankings.map((r, i) => (
              <tr key={r.id || i}
                className={`border-b border-gray-800/50 transition-colors ${
                  i === 0 ? 'bg-yellow-500/10' : i === 1 ? 'bg-gray-400/5' : i === 2 ? 'bg-orange-500/5' : 'hover:bg-gray-800/30'
                }`}>
                <td className="px-4 py-3 font-bold">
                  {i < 3 ? (
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                      i === 0 ? 'bg-yellow-500/20 text-yellow-400' : i === 1 ? 'bg-gray-400/20 text-gray-300' : 'bg-orange-500/20 text-orange-400'
                    }`}>{r.rank}</span>
                  ) : <span className="text-gray-500 pl-2">{r.rank}</span>}
                </td>
                <td className="px-4 py-3 font-medium text-white">{r.competitor_name}</td>
                <td className="px-4 py-3 text-center text-gray-300 text-xs font-bold">{r.nationality}</td>
                <td className="px-4 py-3 text-center text-gray-300 font-bold">{formatPerformance(r.announced_performance, event_type?.unit)}</td>
                <td className="px-4 py-3 text-center text-white font-medium">{formatPerformance(r.realized_performance, event_type?.unit)}</td>
                <td className="px-4 py-3 text-center text-gray-400 font-mono text-xs">{r.performance_time_seconds ? formatTime(r.performance_time_seconds) : '-'}</td>
                <td className="px-4 py-3 text-center"><CardBadge card={r.card_result} /></td>
                <td className="px-4 py-3 text-right font-bold text-lg">
                  {r.is_dq ? <span className="text-red-400">DQ</span> : <span className="text-cyan-400">{r.final_score?.toFixed(1)}</span>}
                </td>
              </tr>
            ))}
            {rankings.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">{t('noResultsYet')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============= COUNTDOWN TIMELINE BANNER =============
function CountdownTimeline({ countdown, countdownConfig, t }) {
  if (!countdownConfig) return null;
  const { duration_seconds, marks, labels } = countdownConfig;
  const clampedCountdown = Math.min(countdown, duration_seconds);
  const isActive = countdown > 0 && countdown <= duration_seconds;

  return (
    <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-700/30" data-testid="countdown-timeline">
      <div className="flex items-center gap-2 mb-3 text-gray-400 text-xs font-medium">
        <Timer className="w-3.5 h-3.5" />
        <span>{t('countdownToOT')}</span>
        <span className="ml-auto font-mono text-white text-sm">
          {countdown > duration_seconds
            ? formatTime(countdown)
            : countdown > 0 ? formatTime(countdown) : 'OT'}
        </span>
      </div>
      {/* Timeline bar */}
      <div className="relative h-10 bg-gray-800/80 rounded-lg overflow-hidden">
        {/* Progress fill — grows from left to right as time elapses */}
        {isActive && (
          <div
            className="absolute inset-y-0 left-0 rounded-lg transition-all duration-1000"
            style={{
              width: `${((duration_seconds - clampedCountdown) / duration_seconds) * 100}%`,
              background: 'linear-gradient(to right, rgba(255,140,50,0.35), rgba(255,100,20,0.2))',
            }}
          />
        )}
        {!isActive && countdown <= 0 && (
          <div
            className="absolute inset-y-0 left-0 rounded-lg w-full"
            style={{ background: 'linear-gradient(to right, rgba(74,222,128,0.25), rgba(34,197,94,0.15))' }}
          />
        )}
        {/* Mark points */}
        <div className="absolute inset-0 flex items-center">
          {marks.map((markSec, idx) => {
            const pos = ((duration_seconds - markSec) / duration_seconds) * 100;
            const isCurrentMark = isActive && (
              idx === marks.length - 1
                ? clampedCountdown === 0
                : clampedCountdown <= markSec && (idx === marks.length - 1 || clampedCountdown > marks[idx + 1])
            );
            const isPast = clampedCountdown < markSec;
            const isOT = markSec === 0;

            return (
              <div
                key={markSec}
                className="absolute flex flex-col items-center"
                style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
              >
                <span className={`text-[10px] font-mono leading-none transition-all ${
                  isCurrentMark
                    ? 'text-white font-bold scale-125'
                    : isPast ? 'text-gray-600' : 'text-gray-400'
                } ${isOT ? 'text-green-400 font-bold' : ''}`}>
                  {labels[idx]}
                </span>
                <div className={`w-0.5 h-2 mt-0.5 rounded-full transition-all ${
                  isCurrentMark
                    ? 'bg-orange-400 w-1 h-3 shadow-[0_0_8px_rgba(255,140,50,0.6)]'
                    : isPast ? 'bg-gray-700' : 'bg-gray-500'
                } ${isOT ? 'bg-green-400' : ''}`} />
                {/* Flashing ring around current mark */}
                {isCurrentMark && (
                  <div className="absolute -inset-2 border-2 border-orange-400/60 rounded-lg animate-pulse" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============= PROTEST PERIOD DISPLAY =============
function ProtestPeriodPanel({ protestPhase, protestCountdown, sessionStatus, t }) {
  if (sessionStatus === 'closed') {
    return (
      <div className="text-center py-12 space-y-6" data-testid="event-terminated-panel">
        <div className="bg-gradient-to-br from-purple-900/60 to-indigo-900/60 border-2 border-purple-500/50 rounded-2xl p-10 max-w-2xl mx-auto">
          <Lock className="w-16 h-16 mx-auto text-purple-400 mb-4" />
          <h2 className="text-4xl font-black text-white mb-3">{t('eventTerminated')}</h2>
          <p className="text-purple-300 text-lg">{t('resultsAreFinal')}</p>
        </div>
      </div>
    );
  }

  if (protestPhase === 'none') return null;

  return (
    <div className="text-center py-4 space-y-4" data-testid="protest-period-panel">
      <div className="flex items-center justify-center gap-2 text-green-400 mb-2">
        <CheckCircle className="w-5 h-5" />
        <span className="font-semibold">{t('allPassagesCompleted')}</span>
      </div>
      {protestPhase === 'before_protest' && (
        <div className="bg-yellow-900/30 border border-yellow-600/40 rounded-lg p-5 max-w-sm mx-auto" data-testid="spectator-before-protest">
          <p className="text-yellow-200 font-semibold mb-2">{t('timeBeforeProtest')}</p>
          <div className="text-3xl font-bold font-mono text-yellow-100 bg-yellow-800/50 rounded-lg py-2 px-5 inline-block">
            {formatTime(protestCountdown)}
          </div>
        </div>
      )}
      {protestPhase === 'protest_active' && (
        <div className="bg-red-900/30 border border-red-600/40 rounded-lg p-5 max-w-sm mx-auto animate-pulse" data-testid="spectator-protest-active">
          <p className="text-red-200 font-bold text-lg mb-2">{t('protestInProgress')}</p>
          <div className="text-3xl font-bold font-mono text-red-100 bg-red-800/50 rounded-lg py-2 px-5 inline-block">
            {formatTime(protestCountdown)}
          </div>
        </div>
      )}
      {protestPhase === 'protest_ended' && (
        <div className="bg-green-900/30 border border-green-600/40 rounded-lg p-4 max-w-sm mx-auto" data-testid="spectator-protest-ended">
          <p className="text-green-300 font-semibold">{t('protestEnded')}</p>
          <p className="text-gray-400 text-sm mt-1">{t('resultsAreFinal')}</p>
        </div>
      )}
    </div>
  );
}

// ============= SPEED COMPARISON CHART =============
function SpeedComparisonChart({ currentCompetitor, runningOrder, t }) {
  const chartData = useMemo(() => {
    const currentSplits = currentCompetitor?.speed_splits || [];
    if (currentSplits.length === 0) return { rows: [], leader: null };

    // Find leader: completed/awaiting passage with the highest realized_performance
    const candidates = (runningOrder || []).filter(p =>
      p.id !== currentCompetitor?.id &&
      ['completed', 'awaiting_results'].includes(p.status) &&
      Array.isArray(p.speed_splits) && p.speed_splits.length > 0
    );
    candidates.sort((a, b) => (b.realized_performance || 0) - (a.realized_performance || 0));
    const leader = candidates[0] || null;

    const buildSegSpeed = (splits, i) => {
      const split = splits[i];
      const prev = i > 0 ? splits[i - 1].elapsed_seconds : 0;
      const seg = split.segment_seconds != null ? split.segment_seconds : (split.elapsed_seconds - prev);
      const segLength = split.segment_length || (split.distance - (i > 0 ? splits[i - 1].distance : 0)) || 25;
      return seg > 0 ? Number((segLength / seg).toFixed(2)) : 0;
    };

    // Merge by distance — union of all distances from both athletes
    const distMap = new Map();
    currentSplits.forEach((s, i) => {
      distMap.set(s.distance, { distance: s.distance, current: buildSegSpeed(currentSplits, i) });
    });
    if (leader) {
      leader.speed_splits.forEach((s, i) => {
        const existing = distMap.get(s.distance) || { distance: s.distance };
        existing.leader = buildSegSpeed(leader.speed_splits, i);
        distMap.set(s.distance, existing);
      });
    }
    const rows = Array.from(distMap.values()).sort((a, b) => a.distance - b.distance);
    return { rows, leader };
  }, [currentCompetitor, runningOrder]);

  if (chartData.rows.length === 0) return null;

  return (
    <div className="mt-4 bg-gradient-to-br from-purple-900/30 to-pink-900/20 border border-purple-600/30 rounded-xl p-4" data-testid="spectator-speed-comparison-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-purple-300" />
          <p className="text-purple-200 text-sm font-semibold">{t('speedComparison')}</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5 bg-cyan-400 rounded-full" />
            <span className="text-cyan-300 font-medium">{t('currentAthlete')}: {currentCompetitor?.competitor_name?.split(' ')[0] || '-'}</span>
          </span>
          {chartData.leader ? (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 bg-yellow-400 rounded-full" style={{ borderTop: '2px dashed' }} />
              <span className="text-yellow-300 font-medium">{t('leader')}: {chartData.leader.competitor_name?.split(' ')[0] || '-'} ({chartData.leader.realized_performance}m)</span>
            </span>
          ) : (
            <span className="text-gray-500">{t('noLeaderYet')}</span>
          )}
        </div>
      </div>
      <div className="h-48 w-full min-h-[180px]" style={{ minWidth: '200px' }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={180} minWidth={200}>
          <LineChart data={chartData.rows} margin={{ top: 5, right: 10, left: 0, bottom: 18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
            <XAxis
              dataKey="distance"
              stroke="#9ca3af"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              label={{ value: t('distanceMeters'), position: 'insideBottom', offset: -10, style: { fill: '#9ca3af', fontSize: 11 } }}
            />
            <YAxis
              stroke="#9ca3af"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickFormatter={(v) => Number(v).toFixed(1)}
              label={{ value: t('speedMs'), angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: 11 } }}
              domain={[0, (dataMax) => Math.ceil((dataMax + 0.3) * 10) / 10]}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #6366f1', borderRadius: 6, fontSize: 12 }}
              labelStyle={{ color: '#a5b4fc' }}
              formatter={(value) => `${Number(value).toFixed(2)} m/s`}
            />
            <Line
              type="monotone"
              dataKey="current"
              name={t('currentAthlete')}
              stroke="#22d3ee"
              strokeWidth={2.5}
              dot={{ r: 3, fill: '#22d3ee' }}
              activeDot={{ r: 5 }}
              isAnimationActive
              animationDuration={400}
            />
            {chartData.leader && (
              <Line
                type="monotone"
                dataKey="leader"
                name={t('leader')}
                stroke="#facc15"
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={{ r: 2.5, fill: '#facc15' }}
                isAnimationActive
                animationDuration={400}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============= LIVE DASHBOARD VIEW =============
function LiveDashboardView({ data, t, serverTimeOffset, protestPhase, protestCountdown }) {
  const { current_competitor, next_competitor, running_order, event_type, progress, event_duration_seconds, countdown: countdownConfig, session } = data;
  const [countdown, setCountdown] = useState(current_competitor?.seconds_to_official_top || 0);
  const [liveElapsed, setLiveElapsed] = useState(0);
  // Remember the last performing/completed competitor to keep results visible until next OT countdown
  const [lastPerfCompetitor, setLastPerfCompetitor] = useState(null);
  const isSTAEvent = event_type?.code === 'STA';

  // Track when a competitor is performing or has results
  useEffect(() => {
    if (current_competitor && ['performing', 'awaiting_results', 'completed'].includes(current_competitor.status)) {
      setLastPerfCompetitor({ ...current_competitor });
    }
  }, [current_competitor?.status, current_competitor?.distance_covered, current_competitor?.performance_time_seconds, current_competitor?.final_score, current_competitor?.card_result]);

  // Clear last perf data only when a new countdown begins (next contestant)
  useEffect(() => {
    if (current_competitor && current_competitor.status === 'waiting' && countdown > 0) {
      setLastPerfCompetitor(null);
    }
  }, [current_competitor?.passage_order, current_competitor?.status, countdown]);

  // Determine which competitor data to display in the hero area
  const displayCompetitor = current_competitor || null;
  const showLastPerfResults = lastPerfCompetitor && displayCompetitor &&
    displayCompetitor.status === 'waiting' && lastPerfCompetitor.passage_order !== displayCompetitor.passage_order;

  // Re-sync countdown from server time offset on every data refresh
  useEffect(() => {
    if (current_competitor?.official_top_time) {
      const otMs = new Date(current_competitor.official_top_time).getTime();
      const nowMs = Date.now() + (serverTimeOffset || 0);
      const secsToOT = Math.max(0, Math.floor((otMs - nowMs) / 1000));
      setCountdown(secsToOT);
    } else if (current_competitor?.seconds_to_official_top != null) {
      setCountdown(current_competitor.seconds_to_official_top);
    }
  }, [current_competitor?.seconds_to_official_top, current_competitor?.official_top_time, serverTimeOffset, data.server_time]);

  // Decrement countdown locally every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Live elapsed timer for performing competitors - compute from actual_start_time with server offset
  useEffect(() => {
    if (current_competitor?.status === 'performing' && current_competitor?.actual_start_time) {
      const startTime = new Date(current_competitor.actual_start_time).getTime();
      const tick = () => {
        const nowMs = Date.now() + (serverTimeOffset || 0);
        setLiveElapsed(Math.max(0, Math.floor((nowMs - startTime) / 1000)));
      };
      tick();
      const timer = setInterval(tick, 1000);
      return () => clearInterval(timer);
    } else if (current_competitor?.performance_time_seconds > 0) {
      setLiveElapsed(Math.round(current_competitor.performance_time_seconds));
    } else {
      setLiveElapsed(0);
    }
  }, [current_competitor?.status, current_competitor?.actual_start_time, current_competitor?.performance_time_seconds, serverTimeOffset]);

  // If event is closed, show terminated message + scoreboard
  if (session?.status === 'closed') {
    return (
      <div className="space-y-6" data-testid="live-dashboard-view">
        <div className="text-center py-12" data-testid="event-terminated-dashboard">
          <div className="bg-gradient-to-br from-purple-900/60 to-indigo-900/60 border-2 border-purple-500/50 rounded-2xl p-10 max-w-2xl mx-auto">
            <Lock className="w-16 h-16 mx-auto text-purple-400 mb-4" />
            <h2 className="text-4xl font-black text-white mb-3">{t('eventTerminated')}</h2>
            <p className="text-purple-300 text-lg">{t('resultsAreFinal')}</p>
          </div>
        </div>
        <ScoreboardView data={data} t={t} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-dashboard-view">
      {/* Current Competitor Hero */}
      <div className="bg-gradient-to-br from-cyan-900/40 to-blue-900/40 rounded-2xl p-6 border border-cyan-700/30">
        <div className="flex items-center gap-2 mb-3">
          <Timer className="w-4 h-4 text-cyan-400" />
          <span className="text-cyan-400 text-sm font-medium">{t('currentCompetitor')}</span>
          {/* Event type code in large text */}
          <span className="mx-auto text-3xl sm:text-4xl font-black tracking-wider text-cyan-300/80">{event_type?.code || ''}</span>
          <span className="text-gray-400 text-sm">#{current_competitor?.passage_order || '-'} {t('of')} {progress?.total || 0}</span>
        </div>
        {current_competitor ? (
          <>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              {/* Contestant Photo */}
              <div className="flex items-center gap-4 flex-1">
                {current_competitor.photo_url ? (
                  <img src={current_competitor.photo_url} alt={current_competitor.competitor_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-cyan-500/50 flex-shrink-0" data-testid="competitor-photo" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-700 to-blue-800 flex items-center justify-center text-white text-xl font-bold border-2 border-cyan-500/30 flex-shrink-0" data-testid="competitor-photo-placeholder">
                    {(current_competitor.competitor_name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-white mb-1">{current_competitor.competitor_name}</h2>
                  <div className="flex items-center gap-3 text-gray-400 text-sm">
                    <span className="font-bold text-white">{current_competitor.nationality}</span>
                    <span className="font-bold">AP: {isSTAEvent && current_competitor.announced_performance ? formatTime(current_competitor.announced_performance) : formatPerformance(current_competitor.announced_performance, event_type?.unit)}</span>
                    {current_competitor.status === 'performing' && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium animate-pulse">{t('performing')}</span>
                    )}
                    {current_competitor.status === 'awaiting_results' && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs font-medium">{t('awaitingResults')}</span>
                    )}
                  </div>
                </div>
              </div>
              {/* Countdown / Live Performance Area - same location */}
              {current_competitor.status === 'performing' ? (
                <div className="flex items-center gap-3 flex-wrap">
                  {!isSTAEvent && (current_competitor.distance_covered > 0 || current_competitor.speed_splits?.length > 0) && (
                    <div className="text-center bg-orange-900/40 rounded-xl px-4 py-2 border border-orange-600/30 min-w-[80px]">
                      <div className="text-xs text-orange-300 mb-0.5">RP</div>
                      <div className="text-xl font-bold font-mono text-orange-200">{formatPerformance(current_competitor.distance_covered || 0, event_type?.unit)}</div>
                    </div>
                  )}
                  <div className="text-center bg-red-900/40 rounded-xl px-4 py-2 border border-red-600/30 min-w-[100px]">
                    <div className="text-xs text-red-300 mb-0.5">{isSTAEvent ? 'RP' : t('duration')}</div>
                    <div className="text-xl font-bold font-mono text-red-200">{formatTime(liveElapsed)}</div>
                  </div>
                  {!isSTAEvent && current_competitor.distance_covered > 0 && liveElapsed > 0 && (
                    <div className="text-center bg-blue-900/40 rounded-xl px-4 py-2 border border-blue-600/30 min-w-[80px]">
                      <div className="text-xs text-blue-300 mb-0.5">{t('speed')}</div>
                      <div className="text-xl font-bold font-mono text-blue-200">{(current_competitor.distance_covered / liveElapsed).toFixed(2)} <span className="text-xs text-gray-400">m/s</span></div>
                    </div>
                  )}
                  <div className="text-center bg-green-900/40 rounded-xl px-4 py-2 border border-green-500/30 min-w-[100px]">
                    <div className="text-xs text-green-300 mb-0.5">{t('status')}</div>
                    <div className="text-lg font-bold text-green-400 animate-pulse">{t('performanceInProgress')}</div>
                  </div>
                </div>
              ) : current_competitor.status === 'awaiting_results' ? (
                <div className="flex items-center gap-3 flex-wrap">
                  {!isSTAEvent && current_competitor.distance_covered > 0 && (
                    <div className="text-center bg-orange-900/40 rounded-xl px-4 py-2 border border-orange-600/30 min-w-[80px]">
                      <div className="text-xs text-orange-300 mb-0.5">RP</div>
                      <div className="text-xl font-bold font-mono text-orange-200">{formatPerformance(current_competitor.distance_covered, event_type?.unit)}</div>
                    </div>
                  )}
                  {liveElapsed > 0 && (
                    <div className="text-center bg-red-900/40 rounded-xl px-4 py-2 border border-red-600/30 min-w-[80px]">
                      <div className="text-xs text-red-300 mb-0.5">{isSTAEvent ? 'RP' : t('duration')}</div>
                      <div className="text-xl font-bold font-mono text-red-200">{formatTime(liveElapsed)}</div>
                    </div>
                  )}
                  {!isSTAEvent && current_competitor.distance_covered > 0 && liveElapsed > 0 && (
                    <div className="text-center bg-blue-900/40 rounded-xl px-4 py-2 border border-blue-600/30 min-w-[80px]">
                      <div className="text-xs text-blue-300 mb-0.5">{t('speed')}</div>
                      <div className="text-xl font-bold font-mono text-blue-200">{(current_competitor.distance_covered / liveElapsed).toFixed(2)} <span className="text-xs text-gray-400">m/s</span></div>
                    </div>
                  )}
                  <div className="text-center bg-yellow-900/40 rounded-xl px-4 py-2 border border-yellow-500/30 min-w-[140px]">
                    <div className="text-xs text-yellow-300 mb-0.5">{t('status')}</div>
                    <div className="text-base font-bold text-yellow-400">{t('validationByJudges')}</div>
                  </div>
                </div>
              ) : current_competitor.status === 'completed' ? (
                <div className="flex items-center gap-3 flex-wrap">
                  {!isSTAEvent && current_competitor.distance_covered > 0 && (
                    <div className="text-center bg-orange-900/40 rounded-xl px-4 py-2 border border-orange-600/30 min-w-[80px]">
                      <div className="text-xs text-orange-300 mb-0.5">RP</div>
                      <div className="text-xl font-bold font-mono text-orange-200">{formatPerformance(current_competitor.distance_covered, event_type?.unit)}</div>
                    </div>
                  )}
                  {current_competitor.performance_time_seconds > 0 && (
                    <div className="text-center bg-red-900/40 rounded-xl px-4 py-2 border border-red-600/30 min-w-[80px]">
                      <div className="text-xs text-red-300 mb-0.5">{isSTAEvent ? 'RP' : t('duration')}</div>
                      <div className="text-xl font-bold font-mono text-red-200">{formatTime(current_competitor.performance_time_seconds)}</div>
                    </div>
                  )}
                  {current_competitor.final_score != null && (
                    <div className="text-center bg-emerald-900/40 rounded-xl px-4 py-2 border border-emerald-600/30 min-w-[80px]">
                      <div className="text-xs text-emerald-300 mb-0.5">{t('score')}</div>
                      <div className="text-xl font-bold font-mono text-emerald-200">{current_competitor.final_score}</div>
                    </div>
                  )}
                  {current_competitor.card_result && (
                    <div className={`text-center rounded-xl px-4 py-2 border min-w-[80px] ${current_competitor.card_result === 'RED' ? 'bg-red-900/40 border-red-600/30' : current_competitor.card_result === 'YELLOW' ? 'bg-yellow-900/40 border-yellow-600/30' : 'bg-white/10 border-white/20'}`}>
                      <div className="text-xs text-gray-300 mb-0.5">{t('card')}</div>
                      <div className={`text-xl font-bold ${current_competitor.card_result === 'RED' ? 'text-red-400' : current_competitor.card_result === 'YELLOW' ? 'text-yellow-400' : 'text-white'}`}>{current_competitor.card_result}</div>
                    </div>
                  )}
                  <div className="text-center bg-cyan-900/40 rounded-xl px-4 py-2 border border-cyan-500/30 min-w-[100px]">
                    <div className="text-xs text-cyan-300 mb-0.5">{t('status')}</div>
                    <div className="text-base font-bold text-cyan-400">{t('performanceSaved')}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Show last performance results during countdown to next contestant */}
                  {showLastPerfResults && lastPerfCompetitor && (
                    <div className="flex items-center gap-2 mr-4 opacity-80">
                      <span className="text-gray-400 text-xs">{lastPerfCompetitor.competitor_name}:</span>
                      {!isSTAEvent && lastPerfCompetitor.distance_covered > 0 && (
                        <span className="text-orange-300 font-mono text-sm">RP {formatPerformance(lastPerfCompetitor.distance_covered, event_type?.unit)}</span>
                      )}
                      {lastPerfCompetitor.performance_time_seconds > 0 && (
                        <span className="text-red-300 font-mono text-sm">{isSTAEvent ? 'RP' : ''} {formatTime(lastPerfCompetitor.performance_time_seconds)}</span>
                      )}
                      {lastPerfCompetitor.final_score != null && (
                        <span className="text-emerald-300 font-mono text-sm">{lastPerfCompetitor.final_score}pts</span>
                      )}
                      {lastPerfCompetitor.card_result && (
                        <span className={`font-bold text-sm ${lastPerfCompetitor.card_result === 'RED' ? 'text-red-400' : lastPerfCompetitor.card_result === 'YELLOW' ? 'text-yellow-400' : 'text-white'}`}>{lastPerfCompetitor.card_result}</span>
                      )}
                    </div>
                  )}
                  <div className="text-center bg-gray-900/60 rounded-xl px-6 py-3 border border-gray-700/50 min-w-[160px]">
                    <div className="text-xs text-gray-400 mb-1">
                      {countdown > 0 ? t('countdownToOT') : t('officialTop')}
                    </div>
                    <div className={`font-mono font-bold ${
                      countdown <= 0
                        ? 'text-green-400 text-lg'
                        : countdown <= 10 ? 'text-red-400 animate-pulse text-3xl' : countdown <= 30 ? 'text-yellow-400 text-3xl' : 'text-white text-3xl'
                    }`}>
                      {countdown > 0
                        ? `-${formatTime(countdown)}`
                        : t('performanceInProgress')}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Countdown Timeline Banner */}
            {countdown > 0 && (
              <div className="mt-4">
                <CountdownTimeline countdown={countdown} countdownConfig={countdownConfig} t={t} />
              </div>
            )}
            {/* Live Performance Splits Panel - stays visible for performing, awaiting_results, completed (not for STA) */}
            {!isSTAEvent && ['performing', 'awaiting_results', 'completed'].includes(current_competitor.status) && current_competitor.speed_splits && current_competitor.speed_splits.length > 0 && (
              <div className="mt-4 bg-indigo-900/30 border border-indigo-600/30 rounded-xl p-4" data-testid="spectator-speed-splits-panel">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-indigo-300 text-sm font-semibold">{t('speedSplits') || '25m Speed Splits'}</p>
                  {current_competitor.distance_covered > 0 && current_competitor.performance_time_seconds > 0 && (
                    <p className="text-blue-300 text-sm font-mono">
                      {t('globalSpeed') || 'Global'}: <span className="text-white font-bold">{(current_competitor.distance_covered / current_competitor.performance_time_seconds).toFixed(2)} m/s</span>
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                  {current_competitor.speed_splits.map((split, i) => {
                    const prevElapsed = i > 0 ? current_competitor.speed_splits[i - 1].elapsed_seconds : 0;
                    const segSeconds = split.segment_seconds != null ? split.segment_seconds : (split.elapsed_seconds - prevElapsed);
                    const segLength = split.segment_length || (split.distance - (i > 0 ? current_competitor.speed_splits[i - 1].distance : 0)) || 25;
                    const segSpeed = segSeconds > 0 ? (segLength / segSeconds) : 0;
                    return (
                      <div key={i} className="bg-indigo-800/40 rounded-lg px-2 py-2 text-center" data-testid={`spectator-split-${i}`}>
                        <p className="text-indigo-200 text-xs font-bold mb-0.5">{split.distance}m</p>
                        <p className="text-white font-mono text-sm">{segSeconds.toFixed(1)}s</p>
                        <p className="text-cyan-300 font-mono text-xs font-semibold">{segSpeed.toFixed(2)} m/s</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Speed Comparison Chart (current vs leader) */}
            {!isSTAEvent && ['performing', 'awaiting_results', 'completed'].includes(current_competitor.status) && current_competitor.speed_splits && current_competitor.speed_splits.length > 0 && (
              <SpeedComparisonChart currentCompetitor={current_competitor} runningOrder={running_order} t={t} />
            )}
          </>
        ) : (
          <ProtestPeriodPanel protestPhase={protestPhase} protestCountdown={protestCountdown} sessionStatus={session?.status} t={t} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Next Competitor */}
        <div className="bg-gray-900/40 rounded-xl p-4 border border-gray-700/30">
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <ChevronRight className="w-3 h-3" />
            <span>{t('upNext')}</span>
          </div>
          {next_competitor ? (
            <>
              <div className="text-white font-medium">{next_competitor.competitor_name}</div>
              <div className="text-gray-400 text-sm"><span className="font-bold text-gray-300">{next_competitor.nationality}</span> &middot; <span className="font-bold">AP: {formatPerformance(next_competitor.announced_performance, event_type?.unit)}</span></div>
            </>
          ) : <span className="text-gray-600 text-sm">{t('noMoreCompetitors')}</span>}
        </div>

        {/* Event Progress */}
        <div className="bg-gray-900/40 rounded-xl p-4 border border-gray-700/30">
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <Users className="w-3 h-3" />
            <span>{t('progress')}</span>
          </div>
          <div className="text-white font-bold text-lg">{progress?.completed || 0} / {progress?.total || 0}</div>
          <div className="w-full bg-gray-800 rounded-full h-2 mt-2">
            <div className="bg-cyan-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress?.percentage || 0}%` }} />
          </div>
        </div>

        {/* Event Duration */}
        <div className="bg-gray-900/40 rounded-xl p-4 border border-gray-700/30">
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <Clock className="w-3 h-3" />
            <span>{t('eventDuration')}</span>
          </div>
          <div className="text-white font-bold text-lg font-mono">{formatTime(event_duration_seconds || 0)}</div>
        </div>
      </div>

      {/* Running Order */}
      <div>
        <div className="flex items-center gap-2 mb-3 text-gray-400 text-sm">
          <List className="w-4 h-4" />
          <span>{t('runningOrder')}</span>
        </div>
        <div className="bg-gray-900/40 rounded-xl border border-gray-700/30 overflow-hidden">
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-900">
                <tr className="text-gray-500 text-xs">
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">{t('competitor')}</th>
                  <th className="px-3 py-2 text-center">{t('nationality') || 'Nat.'}</th>
                  <th className="px-3 py-2 text-center">AP</th>
                  <th className="px-3 py-2 text-center">{t('warmup') || 'W-up'}</th>
                  <th className="px-3 py-2 text-center">{t('transit') || 'Trans.'}</th>
                  <th className="px-3 py-2 text-center">{t('official') || 'Off.'}</th>
                  <th className="px-3 py-2 text-center">RP</th>
                  <th className="px-3 py-2 text-center">{t('duration')}</th>
                  <th className="px-3 py-2 text-center">{t('speed')}</th>
                  <th className="px-3 py-2 text-center">{t('status')}</th>
                  <th className="px-3 py-2 text-right">{t('score')}</th>
                </tr>
              </thead>
              <tbody>
                {running_order.map((p) => (
                  <tr key={p.id}
                    className={`border-b border-gray-800/30 ${
                      p.passage_order === current_competitor?.passage_order ? 'bg-cyan-900/20' : ''
                    }`}>
                    <td className="px-3 py-2 text-gray-500">{p.passage_order}</td>
                    <td className="px-3 py-2 text-white font-medium">{p.competitor_name}</td>
                    <td className="px-3 py-2 text-center text-gray-300 text-xs font-bold">{p.nationality || '-'}</td>
                    <td className="px-3 py-2 text-center text-gray-400">{isSTAEvent ? formatTime(p.announced_performance) : formatPerformance(p.announced_performance, event_type?.unit)}</td>
                    <td className="px-3 py-2 text-center text-gray-500 font-mono text-xs">{formatTimeHHMM(p.warmup_time)}</td>
                    <td className="px-3 py-2 text-center text-gray-500 font-mono text-xs">{formatTimeHHMM(p.transition_time)}</td>
                    <td className="px-3 py-2 text-center text-gray-500 font-mono text-xs">{formatTimeHHMM(p.countdown_time)}</td>
                    <td className="px-3 py-2 text-center text-white font-medium">
                      {isSTAEvent
                        ? (p.performance_time_seconds ? formatTime(p.performance_time_seconds) : (p.distance_covered != null ? formatTime(p.distance_covered) : '-'))
                        : (p.realized_performance != null ? formatPerformance(p.realized_performance, event_type?.unit) : '-')}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-400 font-mono text-xs">
                      {isSTAEvent
                        ? (p.performance_time_seconds ? formatTime(p.performance_time_seconds) : (p.distance_covered != null ? formatTime(p.distance_covered) : '-'))
                        : (p.performance_time_seconds ? formatTime(p.performance_time_seconds) : '-')}
                    </td>
                    <td className="px-3 py-2 text-center text-blue-300 font-mono text-xs">
                      {isSTAEvent ? '-' : (p.realized_performance > 0 && p.performance_time_seconds > 0 ? `${(p.realized_performance / p.performance_time_seconds).toFixed(2)}` : '-')}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.status === 'completed' ? (
                        <span className="text-green-400 text-xs">{t('done')}</span>
                      ) : p.status === 'performing' ? (
                        <span className="text-cyan-400 text-xs animate-pulse">{t('live')}</span>
                      ) : p.status === 'awaiting_results' ? (
                        <span className="text-yellow-400 text-xs">{t('awaiting')}</span>
                      ) : (
                        <span className="text-gray-600 text-xs">{t('waiting')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {p.final_score != null ? (
                        p.is_dq ? <span className="text-red-400">DQ</span> : <span className="text-cyan-400">{p.final_score.toFixed(1)}</span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============= MAIN SPECTATOR VIEW =============
export default function SpectatorView() {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' | 'scoreboard'
  const [wsConnected, setWsConnected] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [presentationMode, setPresentationMode] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [lang, setLang] = useState('en');
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const [protestPhase, setProtestPhase] = useState('none');
  const [protestCountdown, setProtestCountdown] = useState(0);
  const protestSettingsRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const containerRef = useRef(null);
  const rotateRef = useRef(null);

  const t = useCallback((key) => spectatorTranslations[lang]?.[key] || spectatorTranslations.en[key] || key, [lang]);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/spectator/session/${sessionId}`);
      if (!res.ok) throw new Error('Session not found');
      const d = await res.json();
      setData(d);
      setError(null);

      // Sync server time offset
      if (d.server_time) {
        const serverMs = new Date(d.server_time).getTime();
        const clientMs = Date.now();
        setServerTimeOffset(serverMs - clientMs);
      }

      // Sync protest state from server data
      if (d.protest_settings) protestSettingsRef.current = d.protest_settings;
      const session = d.session;
      if (session?.status === 'closed') {
        setProtestPhase('closed');
        setProtestCountdown(0);
      } else if (session?.all_completed_at) {
        const completedMs = new Date(session.all_completed_at).getTime();
        const nowMs = d.server_time ? new Date(d.server_time).getTime() : Date.now();
        const elapsedSec = Math.floor((nowMs - completedMs) / 1000);
        const beforeProtestSec = (d.protest_settings?.before_protest_minutes || 15) * 60;
        const protestDurationSec = (d.protest_settings?.protest_duration_minutes || 15) * 60;

        if (elapsedSec < beforeProtestSec) {
          setProtestPhase('before_protest');
          setProtestCountdown(beforeProtestSec - elapsedSec);
        } else if (elapsedSec < beforeProtestSec + protestDurationSec) {
          setProtestPhase('protest_active');
          setProtestCountdown(beforeProtestSec + protestDurationSec - elapsedSec);
        } else {
          setProtestPhase('protest_ended');
          setProtestCountdown(0);
        }
      }
    } catch (e) {
      setError(e.message);
    }
  }, [sessionId]);

  // Local protest countdown timer (decrements between fetches)
  useEffect(() => {
    if (protestPhase === 'before_protest' || protestPhase === 'protest_active') {
      const timer = setInterval(() => {
        setProtestCountdown(prev => {
          if (prev <= 1) {
            if (protestPhase === 'before_protest') {
              setProtestPhase('protest_active');
              const dur = (protestSettingsRef.current?.protest_duration_minutes || 15) * 60;
              return dur;
            }
            setProtestPhase('protest_ended');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [protestPhase]);

  // Initial fetch + polling fallback
  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(fetchData, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  // WebSocket connection
  useEffect(() => {
    if (!sessionId) return;
    const wsProtocol = API.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/live/${sessionId}`;

    let reconnectTimeout;
    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          clearInterval(pollRef.current);
          pollRef.current = setInterval(fetchData, 5000);
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'viewer_count') {
              setViewerCount(msg.count);
            } else if (['score_update', 'passage_completed', 'performance_started', 'performance_ended', 'session_started', 'session_closed', 'live_performance_update'].includes(msg.type)) {
              fetchData();
            }
          } catch {}
        };

        ws.onclose = () => {
          setWsConnected(false);
          clearInterval(pollRef.current);
          pollRef.current = setInterval(fetchData, 5000);
          reconnectTimeout = setTimeout(connect, 3000);
        };

        ws.onerror = () => { ws.close(); };
      } catch {
        reconnectTimeout = setTimeout(connect, 3000);
      }
    };

    connect();

    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(heartbeat);
      clearTimeout(reconnectTimeout);
      if (wsRef.current) wsRef.current.close();
    };
  }, [sessionId, fetchData]);

  // Presentation mode: auto-rotate views
  useEffect(() => {
    if (!presentationMode) {
      if (rotateRef.current) clearInterval(rotateRef.current);
      return;
    }
    rotateRef.current = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setViewMode(prev => prev === 'dashboard' ? 'scoreboard' : 'dashboard');
        setTimeout(() => setTransitioning(false), 50);
      }, 400);
    }, 15000);
    return () => clearInterval(rotateRef.current);
  }, [presentationMode]);

  // Fullscreen API + Escape key handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setPresentationMode(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && presentationMode) {
        exitPresentation();
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [presentationMode]);

  const enterPresentation = async () => {
    setPresentationMode(true);
    try {
      if (containerRef.current?.requestFullscreen) {
        await containerRef.current.requestFullscreen();
      }
    } catch {}
  };

  const exitPresentation = () => {
    setPresentationMode(false);
    if (rotateRef.current) clearInterval(rotateRef.current);
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    } catch {}
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Session Not Found</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { competition, event_type, session, progress } = data;

  return (
    <div ref={containerRef} className={`min-h-screen bg-gray-950 text-gray-200 ${presentationMode ? 'overflow-hidden' : ''}`} data-testid="spectator-view">
      {/* Header - hidden in presentation mode */}
      {!presentationMode && (
        <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800/50 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              {/* Logos */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {competition.logo_url && (
                  <img src={competition.logo_url} alt="Competition" className="h-10 w-10 object-contain rounded" />
                )}
                {competition.federation_logo_url && (
                  <img src={competition.federation_logo_url} alt={competition.federation} className="h-9 w-auto object-contain opacity-80"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                )}
              </div>

              {/* Competition Info */}
              <div className="flex-1 text-center">
                <h1 className="text-base sm:text-lg font-bold text-white leading-tight">
                  {competition.name}
                </h1>
                <p className="text-xs sm:text-sm text-gray-400">
                  {event_type.name} &middot; {competition.location}
                  {session.status === 'active' && (
                    <span className="ml-2 text-green-400 font-medium">LIVE</span>
                  )}
                </p>
              </div>

              {/* Language Switcher + Connection */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  data-testid="lang-toggle-btn"
                  onClick={() => setLang(l => l === 'en' ? 'fr' : 'en')}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-gray-800/60 border border-gray-700/50 hover:border-cyan-600/40 text-gray-300 hover:text-white transition-all"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{lang === 'en' ? 'FR' : 'EN'}</span>
                </button>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Eye className="w-3.5 h-3.5" />
                  <span>{viewerCount}</span>
                </div>
                <div className={`flex items-center gap-1 text-xs ${wsConnected ? 'text-green-400' : 'text-yellow-500'}`}>
                  {wsConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">{wsConnected ? 'Live' : 'Polling'}</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* View Toggle + Presentation Button */}
      {!presentationMode && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 bg-gray-900/40 rounded-lg p-1 w-fit border border-gray-800/50">
              <button
                data-testid="toggle-dashboard"
                onClick={() => setViewMode('dashboard')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'dashboard' ? 'bg-cyan-600 text-white' : 'text-gray-400 hover:text-white'
                }`}>
                <BarChart3 className="w-4 h-4" />
                <span>{t('liveDashboard')}</span>
              </button>
              <button
                data-testid="toggle-scoreboard"
                onClick={() => setViewMode('scoreboard')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'scoreboard' ? 'bg-cyan-600 text-white' : 'text-gray-400 hover:text-white'
                }`}>
                <Trophy className="w-4 h-4" />
                <span>{t('scoreboard')}</span>
              </button>
            </div>
            <button
              data-testid="enter-presentation-btn"
              onClick={enterPresentation}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white bg-gray-900/40 border border-gray-700/50 hover:border-cyan-600/50 transition-all"
            >
              <Monitor className="w-4 h-4" />
              <span className="hidden sm:inline">{t('presentationMode')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Presentation Mode Header */}
      {presentationMode && (
        <div className="px-8 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {competition.logo_url && (
                <img src={competition.logo_url} alt="Competition" className="h-12 w-12 object-contain rounded" />
              )}
              <div>
                <h1 className="text-2xl font-bold text-white">{competition.name}</h1>
                <p className="text-sm text-gray-400">
                  {event_type.name} &middot; {competition.location}
                  {session.status === 'active' && (
                    <span className="ml-2 text-green-400 font-medium">LIVE</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                data-testid="pres-lang-toggle-btn"
                onClick={() => setLang(l => l === 'en' ? 'fr' : 'en')}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-gray-800/60 border border-gray-700/50 text-gray-300 hover:text-white transition-all"
              >
                <Globe className="w-3.5 h-3.5" />
                <span>{lang === 'en' ? 'FR' : 'EN'}</span>
              </button>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                {viewMode === 'dashboard' ? <BarChart3 className="w-4 h-4 text-cyan-400" /> : <Trophy className="w-4 h-4 text-yellow-400" />}
                <span className="text-white font-medium">{viewMode === 'dashboard' ? t('liveDashboard') : t('scoreboard')}</span>
              </div>
              <button
                data-testid="exit-presentation-btn"
                onClick={exitPresentation}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white bg-gray-800/60 border border-gray-700/50 hover:border-red-500/50 transition-all"
              >
                <Minimize className="w-3.5 h-3.5" />
                <span>{t('exit')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <main className={`${presentationMode ? 'px-8 py-4' : 'max-w-6xl mx-auto px-4 py-6'}`}>
        <div
          className={`transition-opacity duration-400 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
          style={{ transitionDuration: '400ms' }}
        >
          {viewMode === 'dashboard' ? (
            <LiveDashboardView data={data} t={t} serverTimeOffset={serverTimeOffset} protestPhase={protestPhase} protestCountdown={protestCountdown} />
          ) : (
            <ScoreboardView data={data} t={t} />
          )}
        </div>
      </main>

      {/* Footer - hidden in presentation mode */}
      {!presentationMode && (
        <footer className="text-center text-xs text-gray-600 py-4 border-t border-gray-800/30">
          Apnea Coach Manager &middot; {competition.federation && `${competition.federation} Rules`}
        </footer>
      )}

      {/* Presentation mode: auto-rotate indicator */}
      {presentationMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className={`w-2 h-2 rounded-full transition-all ${viewMode === 'dashboard' ? 'bg-cyan-400 scale-125' : 'bg-gray-600'}`} />
            <div className={`w-2 h-2 rounded-full transition-all ${viewMode === 'scoreboard' ? 'bg-cyan-400 scale-125' : 'bg-gray-600'}`} />
          </div>
          <span className="text-xs text-gray-500">{t('autoRotating')}</span>
        </div>
      )}
    </div>
  );
}
